/**
 * THE DATA LAYER — one fan-out, keyed per endpoint, aborted on change, budgeted.
 *
 * Four properties, and each one is a defect on the screens this replaces:
 *
 *  1. KEYED PER ENDPOINT. A request is cached under the fields IT reads
 *     (filter-slices.ts), so changing a Records-only criterion moves no chart,
 *     the dwell subject moves only the dwell panel, and SWITCHING TABS REFETCHES
 *     NOTHING that is already loaded. The analytics screen had to hand-build a
 *     narrow version of this to stop the dwell subject re-running five requests.
 *
 *  2. ABORTED ON CHANGE. A reader dragging a range chip generates a request per
 *     stop; each one holds a pooled connection for its life against a production
 *     pool of five. The in-flight one is aborted and the queued one is dropped
 *     before it is sent.
 *
 *  3. STALE-WHILE-REVALIDATE. Existing data stays visible and dims rather than
 *     being replaced by a skeleton. What is on screen is still the last true
 *     answer, and blanking it is a worse lie than showing it a moment stale.
 *
 *  4. FAILURE IS PER SLICE. One 500 never blanks another panel, and each slice
 *     carries its own retry — never a reload of the whole screen to recover from
 *     one panel.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  AnalyticsBatchResponse,
  AnalyticsLatency,
  AnalyticsMeta,
  AnalyticsOverview,
  AnalyticsQuality,
  AnalyticsQualityDrill,
  AnalyticsRhythm,
  AnalyticsScopeQuery,
  AnalyticsTurnover,
  fetchAnalyticsBatch,
  fetchAnalyticsLatency,
  fetchAnalyticsMeta,
  fetchAnalyticsOverview,
  fetchAnalyticsQuality,
  fetchAnalyticsQualityDrill,
  fetchAnalyticsRhythm,
  fetchAnalyticsTurnover,
} from '../../../../../api/analytics-client';
import {
  ActivityFeedResponse,
  CorrectionsResponse,
  fetchActivity,
  fetchCorrections,
} from '../../../../../api/activity-client';
import { DashboardFilter, CUSTOM_RANGE, filterMetaFrom } from '../state/filter-model';
import {
  SLICES_FOR_TAB,
  SLICE_PRIORITY,
  SliceName,
  sliceKey,
} from '../state/filter-slices';
import { buildPlan } from './panel-plan';
import {
  DayLogsResponse,
  RecordsResponse,
  fetchDayLogs,
  fetchRecords,
  rangeCoverage,
} from './records-api';
import { deskDayOf } from '../../../../../function-utils/building-time';
import { useClock } from '../../../../../context/clock-context';
import { budget, isSuperseded } from './request-budget';

export const RECORDS_PAGE_SIZE = 50;
export const ACTIVITY_PAGE_SIZE = 25;

export interface Slice<T> {
  data?: T;
  error?: string;
  loading: boolean;
  /** A refetch is in flight over data that is still true. */
  stale: boolean;
  retry: () => void;
}

export interface DashboardData {
  meta: Slice<AnalyticsMeta>;
  overview: Slice<AnalyticsOverview>;
  batch: Slice<AnalyticsBatchResponse>;
  latency: Slice<AnalyticsLatency>;
  quality: Slice<AnalyticsQuality>;
  qualityDrill: Slice<AnalyticsQualityDrill>;
  turnover: Slice<AnalyticsTurnover>;
  rhythm: Slice<AnalyticsRhythm>;
  activity: Slice<ActivityFeedResponse>;
  records: Slice<RecordsResponse>;
  corrections: Slice<CorrectionsResponse>;
  logs: Slice<DayLogsResponse>;
  /* Comparison mode. Absent — not empty — when no second scope is chosen. */
  overviewB: Slice<AnalyticsOverview>;
  batchB: Slice<AnalyticsBatchResponse>;
}

const IDLE: Slice<never> = {
  loading: false,
  stale: false,
  retry: () => undefined,
};

/** Which slices talk to `/analytics/*`, i.e. count against the tighter limit. */
const ANALYTICS_SLICES = new Set<SliceName>([
  'meta',
  'overview',
  'batch',
  'latency',
  'quality',
  'qualityDrill',
  'turnover',
  'rhythm',
  'overviewB',
  'batchB',
]);

/**
 * THE SCOPE QUERY EVERY ANALYTICS ROUTE SHARES.
 *
 * A custom window travels as DAY KEYS (`from_day` / `to_day`, inclusive) and
 * then OVERRIDES the range outright — which is how "this quarter", "year to
 * date" and a hand-picked range are expressible at all: none of the three is a
 * member of the server's range enum. The server turns the keys into instants on
 * the reader's clock (R-T5, R-T12); no instant is computed in the browser.
 *
 * `buildingId` and `portfolioId` ARE FILTERS, NOT THE BOUNDARY. Omitting both
 * asks for every building the caller can see and RLS decides what that means;
 * naming a portfolio can only ever NARROW, because the server intersects it with
 * the buildings the caller already reads. A caller cannot widen their own answer
 * by changing a parameter here.
 */
export const scopeQuery = (filter: DashboardFilter): AnalyticsScopeQuery => {
  const custom = filter.range === CUSTOM_RANGE && filter.from && filter.to;

  return {
    range: custom ? undefined : filter.range,
    fromDay: custom ? filter.from : undefined,
    toDay: custom ? filter.to : undefined,
    compare: filter.compare,
    buildingId: filter.buildingId,
    portfolioId: filter.portfolioId,

    /* ── THE REST OF THE FILTER, WHICH USED TO GO NOWHERE ──
     *
     * This function returned six fields. The filter object has thirty, the chip
     * row rendered all of them, and the other twenty-four reached no analytics
     * request at all — so narrowing to Kind = Guest, Unit 4B, 09:00–17:00 left
     * every tile and every chart showing the whole organisation, with the chips
     * on screen saying otherwise.
     *
     * They are sent WHOLE and to every analytics route, because a screen where
     * the kind filter reaches four panels and not the other five is nine answers
     * to nine different questions under one row of chips.
     *
     * Anything a given panel cannot honour comes back named in that panel's
     * `notes` — `name` and `lastName` always do, because visitor names are
     * encrypted with a random IV and are not expressible in SQL. Sending them
     * anyway is what lets the server SAY so; dropping them here would put the
     * silence back. */
    kinds: filter.kinds,
    severity: filter.severity,
    subKind: filter.visitorDetails,
    unit: filter.unit,
    actors: filter.actors,
    role: filter.role,
    approval: filter.approval,
    priority: filter.priority,
    preAuthorized: filter.preAuthorized,
    onPass: filter.onPass,
    parkingValidation: filter.parkingValidation,
    numberOfGuests: filter.numberOfGuests,
    carrier: filter.carrier,
    parcelStatus: filter.parcelStatus,
    status: filter.status,
    direction: filter.direction,
    groups: filter.groups,
    visitorType: filter.visitorType,
    buildingActivity: filter.buildingActivity,
    timeFrom: filter.timeFrom,
    timeTo: filter.timeTo,
    q: filter.q,
    name: filter.name,
    lastName: filter.lastName,
  };
};

/**
 * The comparison scope's query. THE SAME WINDOW, a different place.
 *
 * The two figures on a comparison tile are only comparable if they cover the
 * same period, so every field except the scope is copied from the primary. The
 * period is deliberately not settable per side: two buildings over two different
 * months is a chart of nothing.
 */
export const compareScopeQuery = (filter: DashboardFilter): AnalyticsScopeQuery => ({
  ...scopeQuery(filter),
  buildingId: filter.compareScope?.buildingId ?? null,
  portfolioId: filter.compareScope?.portfolioId ?? null,
});

/**
 * The activity feed's window. It takes calendar days, both inclusive.
 *
 * A custom range is already two day keys and is handed over as it is — this
 * used to slice an ISO instant to ten characters, which is the UTC date and a
 * day off for anyone not on Greenwich.
 *
 * A NAMED range is turned into its two days here, counted from `today` on the
 * reader's clock, because `GET /activity` has no range parameter: without this
 * the Activity tab answered its seven-day default under a chip reading "30
 * days". The two shift ranges cannot be resolved in the browser and send
 * nothing (the server's default window, as before).
 */
export const activityDays = (
  filter: Pick<DashboardFilter, 'range' | 'from' | 'to'>,
  today: string
): { dateFrom?: string; dateTo?: string } => {
  const coverage = rangeCoverage(filter, today);

  return coverage.resolvable && coverage.from && coverage.to
    ? { dateFrom: coverage.from, dateTo: coverage.to }
    : {};
};

interface Entry {
  key: string;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  data?: any;
  /* eslint-enable @typescript-eslint/no-explicit-any */
  error?: string;
  loading: boolean;
}

type State = Partial<Record<SliceName, Entry>>;

export interface UseDataOptions {
  /** Nothing is requested at all without this — the screen is behind a
   *  permission gate and a request that would 403 must not be made. */
  enabled?: boolean;
  /** Drop the analytics slices entirely for a caller without `analytics:read`.
   *  The tab is hidden too; this is the belt. */
  canReadAnalytics?: boolean;
}

export const useDashboardData = (
  filter: DashboardFilter,
  { enabled = true, canReadAnalytics = true }: UseDataOptions = {}
): DashboardData => {
  const [state, setState] = useState<State>({});
  /* The cache is a ref, not state: writing to it must not re-render, and a slice
     whose key is unchanged must be readable synchronously during the render that
     asks for it — which is what makes a tab switch cost nothing. */
  const cache = useRef(new Map<string, Entry>());
  const controllers = useRef(new Map<SliceName, AbortController>());
  /** The key each slice is CURRENTLY meant to be answering. A late response for
   *  a key that has moved on is dropped rather than written. */
  const wanted = useRef(new Map<SliceName, string>());
  const [reloads, setReloads] = useState<Partial<Record<SliceName, number>>>({});

  /* TODAY ON THE READER'S CLOCK — the device's for an owner or a manager, the
     building's for a concierge (R-T12) — for the one request that needs a named
     range spelled out as days (the feed). The DESK day, not the calendar date:
     GET /activity reads a day key as the desk day that starts at 07:00
     (BE src/activity/feed.js deskDayRange), so between midnight and 07:00 the
     calendar date named a desk day that had not begun, and "Today" showed
     none of the night's rows (2026-10-22, verify-quick-out-dashboard). */
  const clock = useClock({ buildingId: filter.buildingId });
  const today = deskDayOf(clock.zone);

  const meta = state.meta?.data as AnalyticsMeta | undefined;
  const filterMeta = useMemo(() => (meta ? filterMetaFrom(meta) : undefined), [meta]);

  const needed = useMemo<SliceName[]>(() => {
    if (!enabled) return [];
    const forTab = SLICES_FOR_TAB[filter.tab] || SLICES_FOR_TAB.overview;
    const list = forTab.filter(name => canReadAnalytics || !ANALYTICS_SLICES.has(name));
    // The drill is fetched only when one is open — it is the one request on this
    // screen whose cost is per click.

    return filter.qualityDrill ? list : list.filter(name => name !== 'qualityDrill');
  }, [enabled, filter.tab, filter.qualityDrill, canReadAnalytics]);

  /** Every needed slice's key, recomputed each render. Cheap: it is a few
   *  JSON.stringify calls over a dozen scalars. */
  const keys = useMemo(() => {
    const map = new Map<SliceName, string>();
    for (const name of needed) map.set(name, sliceKey(name, filter));

    return map;
  }, [needed, filter]);

  const fetcher = useCallback(
    (name: SliceName, signal: AbortSignal): Promise<unknown> => {
      const query = scopeQuery(filter);

      switch (name) {
        case 'meta':
          return fetchAnalyticsMeta({ signal });
        case 'overview':
          return fetchAnalyticsOverview(query, { signal });
        case 'overviewB':
          return fetchAnalyticsOverview(compareScopeQuery(filter), { signal });
        /* THE SAME PLAN, so the two scopes come back with the same panels in the
           same order and a panel can put them side by side without matching ids
           by hand. */
        case 'batchB': {
          if (!filterMeta || !meta) return Promise.resolve(undefined);
          const planB = buildPlan({
            filter,
            meta: filterMeta,
            showPortfolios: filterMeta.portfolios.length > 1,
            maxPanels: meta.limits?.maxPanels ?? 12,
          });
          if (!planB.length) return Promise.resolve(undefined);

          return fetchAnalyticsBatch(planB, compareScopeQuery(filter), { signal });
        }
        case 'batch': {
          if (!filterMeta || !meta) return Promise.resolve(undefined);
          const plan = buildPlan({
            filter,
            meta: filterMeta,
            showPortfolios: filterMeta.portfolios.length > 1,
            maxPanels: meta.limits?.maxPanels ?? 12,
          });
          if (!plan.length) return Promise.resolve(undefined);

          return fetchAnalyticsBatch(plan, query, { signal });
        }
        case 'latency':
          return fetchAnalyticsLatency({ ...query, subject: filter.subject }, { signal });
        case 'quality':
          return fetchAnalyticsQuality(query, { signal });
        case 'qualityDrill':
          return fetchAnalyticsQualityDrill(filter.qualityDrill as string, query, {
            signal,
          });
        case 'turnover':
          return fetchAnalyticsTurnover(
            { ...query, bucket: filter.turnoverBucket },
            { signal }
          );
        case 'rhythm':
          // `kinds` and `severity` used to be spelled out here because the scope
          // query carried neither. It carries both now, along with everything
          // else, so only the measure is added.
          return fetchAnalyticsRhythm({ ...query, measure: filter.measure }, { signal });
        case 'activity':
          return fetchActivity({
            buildingId: filter.buildingId,
            limit: ACTIVITY_PAGE_SIZE * (filter.page + 1),
            group: filter.groups.length
              ? (filter.groups as ActivityFeedResponse['groups'][number]['key'][])
              : undefined,
            kind: filter.kinds.length ? filter.kinds : undefined,
            actor: filter.actors.length ? filter.actors : undefined,
            direction: filter.direction || undefined,
            status: filter.status || undefined,
            unit: filter.unit || undefined,
            person: filter.name || undefined,
            q: filter.q || undefined,
            ...activityDays(filter, today),
          }, { signal });
        case 'records':
          return fetchRecords(filter, RECORDS_PAGE_SIZE, { signal });
        case 'corrections':
          return fetchCorrections(
            {
              limit: ACTIVITY_PAGE_SIZE * (filter.page + 1),
              table:
                filter.book === 'Parcel Log'
                  ? 'package'
                  : filter.book === 'Daily Log'
                  ? 'daily_log'
                  : 'visitor_log',
            },
            { signal }
          );
        case 'logs': {
          if (filter.buildingId == null || !filter.from) {
            return Promise.resolve(undefined);
          }

          return fetchDayLogs(filter.buildingId, filter.from, { signal });
        }
        default:
          return Promise.resolve(undefined);
      }
    },
    [filter, filterMeta, meta, today]
  );

  /**
   * PREREQUISITES.
   *
   * A slice whose inputs are not ready yet is SKIPPED, not requested and not
   * cached. The distinction matters: resolving it to `undefined` and caching
   * that would mark the question answered, and because its key does not change
   * when the prerequisite lands, it would never be asked again — a permanently
   * empty panel on a screen that has finished loading.
   *
   * `batch` is the one that bites. The plan cannot be built without `meta`,
   * because each measure carries its OWN legal dimension list and sending a pair
   * outside it makes the server substitute one and say so — a chart with the
   * wrong title. So the batch waits for the descriptor rather than guessing, and
   * fires on the render after it lands.
   */
  const ready = useCallback(
    (name: SliceName): boolean => {
      switch (name) {
        case 'batch':
          return Boolean(meta && filterMeta);
        /* NOT REQUESTED AT ALL until a second scope is chosen. An un-ready slice
           is skipped rather than resolved to undefined, so turning comparison
           mode on asks the question for the first time rather than reading back
           a cached "nothing". */
        case 'overviewB':
          return Boolean(filter.compareScope) && Boolean(meta && filterMeta);
        case 'batchB':
          return Boolean(filter.compareScope) && Boolean(meta && filterMeta);
        case 'qualityDrill':
          return Boolean(filter.qualityDrill);
        case 'logs':
          return filter.buildingId != null && Boolean(filter.from);
        default:
          return true;
      }
    },
    [
      meta,
      filterMeta,
      filter.qualityDrill,
      filter.buildingId,
      filter.from,
      filter.compareScope,
    ]
  );

  useEffect(() => {
    if (!enabled) return;

    for (const name of needed) {
      const key = keys.get(name) as string;

      if (!ready(name)) continue;

      // `meta` is fetched ONCE, ever. It describes the caller, not the query.
      if (name === 'meta' && cache.current.has(key)) continue;

      const cached = cache.current.get(key);

      /* ═══ ASKED ONCE PER KEY, INCLUDING WHEN THE ANSWER WAS AN ERROR ═══
       *
       * `wanted` is the key this slice is currently answering, and reaching it
       * again means the question has not changed — so there is nothing to do,
       * whatever the outcome was.
       *
       * The guard used to also require "and it is cached or still loading",
       * which quietly excluded the FAILED case: an error is neither. So a slice
       * whose request rejected re-entered on the very next render, set itself
       * back to loading, wiped the error it had just published, requested again,
       * and rejected again — a permanent retry loop that also renders as a panel
       * stuck on its skeleton rather than as the failure it actually is. Found
       * by the per-slice failure test, which could never see the error it was
       * asserting on.
       *
       * A real retry clears this entry, which is what makes the retry button the
       * only way back. */
      if (wanted.current.get(name) === key) continue;

      wanted.current.set(name, key);

      // A cache hit renders synchronously and asks nothing. THIS is what makes a
      // tab switch free.
      if (cached && !cached.error) {
        setState(prev =>
          prev[name] === cached ? prev : { ...prev, [name]: cached }
        );
        continue;
      }

      // Abort whatever this slice was previously asking. Its answer is to a
      // question nobody has any more.
      controllers.current.get(name)?.abort();
      const controller = new AbortController();
      controllers.current.set(name, controller);

      setState(prev => ({
        ...prev,
        [name]: {
          key,
          // Keep the previous data visible while the new answer is in flight.
          data: prev[name]?.data,
          loading: true,
          error: undefined,
        },
      }));

      budget
        .submit({
          priority: SLICE_PRIORITY[name],
          analytics: ANALYTICS_SLICES.has(name),
          stillWanted: () => wanted.current.get(name) === key,
          run: () => fetcher(name, controller.signal),
        })
        .then(data => {
          if (wanted.current.get(name) !== key) return;
          const entry: Entry = { key, data, loading: false };
          cache.current.set(key, entry);
          setState(prev => ({ ...prev, [name]: entry }));
        })
        .catch(error => {
          // A superseded request is not a failure. The reader cancelled it
          // themselves by changing a filter, and rendering "we could not load
          // this" for it is the RC-10 class of bug where an error looked exactly
          // like an empty day.
          if (isSuperseded(error)) return;
          if (wanted.current.get(name) !== key) return;
          setState(prev => ({
            ...prev,
            [name]: {
              key,
              data: prev[name]?.data,
              loading: false,
              error:
                (error as Error)?.message ||
                'This could not be calculated for this period.',
            },
          }));
        });
    }
    // `state` is deliberately absent: including it would re-run this effect on
    // every response and re-evaluate every slice for nothing. The `wanted` ref is
    // what carries the in-flight bookkeeping across renders.
    // `state` is deliberately NOT a dependency here: including it would re-run
    // this effect on every response and re-evaluate every slice for nothing. The
    // `wanted` and `cache` refs carry the in-flight bookkeeping across renders,
    // which is exactly what a ref is for.
    // eslint-disable-next-line
  }, [enabled, needed, keys, fetcher, ready, reloads]);

  /* Abort everything on unmount. A screen the reader has left must not hold a
     pooled connection while it finishes answering. */
  useEffect(
    () => () => {
      controllers.current.forEach(controller => controller.abort());
      controllers.current.clear();
    },
    []
  );

  const retryFor = useCallback(
    (name: SliceName) => () => {
      const key = wanted.current.get(name);
      if (key) cache.current.delete(key);
      wanted.current.delete(name);
      setReloads(prev => ({ ...prev, [name]: (prev[name] || 0) + 1 }));
    },
    []
  );

  const slice = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    <T,>(name: SliceName): Slice<T> => {
      const entry = state[name];
      if (!entry) return { ...IDLE, retry: retryFor(name) } as Slice<T>;

      return {
        data: entry.data as T,
        error: entry.error,
        loading: entry.loading && entry.data === undefined,
        stale: entry.loading && entry.data !== undefined,
        retry: retryFor(name),
      };
    },
    [state, retryFor]
  );

  return useMemo(
    () => ({
      meta: slice<AnalyticsMeta>('meta'),
      overview: slice<AnalyticsOverview>('overview'),
      batch: slice<AnalyticsBatchResponse>('batch'),
      latency: slice<AnalyticsLatency>('latency'),
      quality: slice<AnalyticsQuality>('quality'),
      qualityDrill: slice<AnalyticsQualityDrill>('qualityDrill'),
      turnover: slice<AnalyticsTurnover>('turnover'),
      rhythm: slice<AnalyticsRhythm>('rhythm'),
      activity: slice<ActivityFeedResponse>('activity'),
      records: slice<RecordsResponse>('records'),
      overviewB: slice<AnalyticsOverview>('overviewB'),
      batchB: slice<AnalyticsBatchResponse>('batchB'),
      corrections: slice<CorrectionsResponse>('corrections'),
      logs: slice<DayLogsResponse>('logs'),
    }),
    [slice]
  );
};
